// types/index.ts - Updated to match your backend exactly

export interface User {
  id: number; // Changed from _id: string to match backend
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus; // Added status field to match backend
  employee_id?: string;
  department?: string;
  designation?: string;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  phone?: string;
  mobile?: string;
  address?: string;
  city?: string;
  state?: string;
  division?: string;
  circle?: string;
  zone?: string;
  last_login?: Date;
  login_attempts?: number;
  email_verified: boolean;
  profile_picture?: string;
  createdAt: Date; // Map from created_at
  updatedAt: Date; // Map from updated_at
}

// Updated to match your backend UserRole enum exactly
export enum UserRole {
  SYSTEM_ADMIN = 'system_admin',
  ENGINEER_IN_CHIEF = 'engineer_in_chief',
  EXECUTIVE_ENGINEER = 'executive_engineer',
  ASSISTANT_ENGINEER = 'assistant_engineer',
  EDITOR = 'editor',
  VIEWER = 'viewer',
  // Add other roles from your backend as needed
}

// Updated to match your backend UserStatus enum exactly
export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  PENDING_APPROVAL = 'pending_approval',
  RETIRED = 'retired',
  TRANSFERRED = 'transferred',
}

// Content Types - Updated to match backend
export interface Content {
  id: number; // Changed from _id: string
  title: string;
  content: string;
  excerpt?: string;
  type: ContentType;
  status: ContentStatus;
  author: User; // Keep as User object
  tags: string[];
  attachments: string[];
  publishedAt?: Date;
  featuredImage?: string;
  viewCount: number;
  metadata?: Record<string, any>;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export enum ContentType {
  NEWS = 'news',
  PRESS_RELEASE = 'press_release',
  GO_CIRCULAR = 'go_circular',
}

export enum ContentStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  ARCHIVED = 'archived',
}

// API Types - Updated to match your backend response
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  totalPages: number;
  hasNext?: boolean;
  hasPrev?: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
  remember_me?: boolean; // Match backend field name
}

// Updated to match your backend AuthResponse exactly
export interface LoginResponse {
  access_token: string;
  refresh_token?: string;
  user: {
    id: number;
    name: string;
    email: string;
    role: string;
    status: string;
    employee_id?: string;
    department?: string;
    designation?: string;
  };
  expires_in: number;
  token_type: 'Bearer';
  issued_at: string;
}

// Filter Types
export interface ContentFilter {
  page?: number;
  limit?: number;
  type?: ContentType;
  status?: ContentStatus;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface UserFilter {
  page?: number;
  limit?: number;
  role?: UserRole;
  status?: UserStatus;
  search?: string;
  department?: string;
  division?: string;
  circle?: string;
  zone?: string;
  isActive?: boolean;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

// Form Types - Updated to match backend CreateUserDto
export interface CreateContentRequest {
  title: string;
  content: string;
  excerpt?: string;
  type: ContentType;
  status?: ContentStatus;
  tags?: string[];
  featuredImage?: string;
  metadata?: Record<string, any>;
}

export interface CreateUserRequest {
  name: string;
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  role?: UserRole;
  status?: UserStatus;
  employee_id?: string;
  department?: string;
  designation?: string;
  phone?: string;
  mobile?: string;
  address?: string;
  city?: string;
  state?: string;
  division?: string;
  circle?: string;
  zone?: string;
}

// Upload Types
export interface UploadResponse {
  filename: string;
  originalname: string;
  size: number;
  path: string;
  url: string;
}

// Common Types
export interface SelectOption {
  value: string | number;
  label: string;
}

export interface TableColumn<T> {
  key: keyof T;
  title: string;
  render?: (value: any, record: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
}

export interface MenuItem {
  key: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  path?: string;
  children?: MenuItem[];
}

// Notification Types
export interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  timestamp: Date;
}

// Stats Types - Updated to match your backend
export interface DashboardStats {
  totalContent: number;
  publishedContent: number;
  draftContent: number;
  archivedContent?: number;
  totalViews: number;
  recentActivities?: Activity[];
}

export interface UserSummary {
  totalUsers: number;
  activeUsers: number;
  pendingUsers: number;
  suspendedUsers: number;
  inactiveUsers: number;
  retiredUsers: number;
  transferredUsers: number;
  emailVerifiedUsers: number;
  lockedUsers: number;
  recentlyActiveUsers: number;
}

export interface Activity {
  id: string;
  action: string;
  item: string;
  user: string;
  timestamp: Date;
  type: ContentType;
}

// Settings Types
export interface AppSettings {
  siteName: string;
  siteDescription: string;
  adminEmail: string;
  maxFileSize: number;
  allowedFileTypes: string[];
  autoPublish: boolean;
  emailNotifications: boolean;
  maintenanceMode: boolean;
}

// Error Types
export interface ApiError {
  message: string;
  statusCode: number;
  timestamp: string;
  path: string;
}

// Pagination Types
export interface PaginationProps {
  current: number;
  total: number;
  pageSize: number;
  onChange: (page: number, pageSize?: number) => void;
  showSizeChanger?: boolean;
  showQuickJumper?: boolean;
  showTotal?: (total: number, range: [number, number]) => string;
}

// Form Validation Types
export interface ValidationRule {
  required?: boolean;
  min?: number;
  max?: number;
  pattern?: RegExp;
  validator?: (value: any) => string | undefined;
}

export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'password' | 'number' | 'textarea' | 'select' | 'file';
  rules?: ValidationRule[];
  options?: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
}

// Theme Types
export interface Theme {
  colors: {
    primary: string;
    secondary: string;
    background: string;
    surface: string;
    text: string;
    textSecondary: string;
    border: string;
    error: string;
    warning: string;
    success: string;
    info: string;
  };
  typography: {
    fontFamily: string;
    fontSize: {
      xs: string;
      sm: string;
      base: string;
      lg: string;
      xl: string;
      '2xl': string;
      '3xl': string;
    };
  };
  spacing: {
    xs: string;
    sm: string;
    md: string;
    lg: string;
    xl: string;
  };
}